// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";

import { AlertBox } from "./hooks/alert_box.js";
import { AnimateFavorites } from "./hooks/animate_favorites.js";
import { AudioWave } from "./hooks/audio_wave.js";
import { AutocompleteInput } from "./hooks/autocomplete_input.js";
import { AutoWidthInput } from "./hooks/auto_width_input.js";
import { Banner } from "./hooks/banner";
import { BodyScroll } from "./hooks/body_scroll.js";
import { Bumper } from "./hooks/bumper.js";
import { CopyText } from "./hooks/copy_text";
import { DateRangePreview } from "./hooks/date_range_preview.js";
import { Diff } from "./hooks/diff.js";
import { DonationAmount } from "./hooks/donation_amount.js";
import { DonutChart } from "./hooks/donut_chart.js";
import { DragScroll } from "./hooks/drag_scroll.js";
import { Flash } from "./hooks/flash";
import { FocusElement } from "./hooks/focus_element.js";
import { IframeResize } from "./hooks/iframe_resize.js";
import { KeyboardAvoidingPopover } from "./hooks/keyboard_avoiding_popover.js";
import { LoadMoreOnScroll } from "./hooks/load_more_on_scroll.js";
import { ManageVideoPlayer } from "./hooks/manage_video_player.js";
import { Markdown } from "./hooks/markdown.js";
import { MarkdownComplete } from "./hooks/markdown_complete.js";
import { OrderPlaylist } from "./hooks/order_playlist.js";
import { PwywSlider } from "./hooks/pwyw_slider.js";
import { ResponsiveDropdown } from "./hooks/responsive_dropdown.js";
import { ScrollIntoViewOnFocus } from "./hooks/scroll_into_view_on_focus.js";
import { SearchSuggestions } from "./hooks/search_suggestions.js";
import { StickyIfFits } from "./hooks/sticky_if_fits.js";
import {
  Suggestion,
  preserveFocusedTranscriptTextAreaScroll,
} from "./hooks/suggestion";
import { SyncCursorVideo } from "./hooks/sync_cursor_video.js";
import { TallyEmbed } from "./hooks/tally_embed.js";
import { ThemeToggle } from "./hooks/theme_toggle.js";
import { ThumbnailPreviewVideo } from "./hooks/thumbnail_preview_video.js";
import { TimeInput } from "./hooks/timeinput.js";
import { Tooltip } from "./hooks/tooltip.js";
import { Transcript } from "./hooks/transcript.js";
import { Video, setCanAutoplay } from "./hooks/video.js";
import { WebShare } from "./hooks/web_share.js";
import { WhatAreOthersWatchingUpdates } from "./hooks/what_are_others_watching_updates.js";

import posthog from "posthog-js";
const hasAnalyticsConsent = (() => {
  const cookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith("cookieyes-consent="));
  if (!cookie) return false;
  const value = decodeURIComponent(cookie.split("=")[1]);
  return value.split(",").some((part) => part.trim() === "analytics:yes");
})();
const posthogAutocaptureSampleRate = 0.7;

const bucketSeed = (seed) => {
  if (!seed) return 0;

  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }

  return hash / 4294967296;
};

if (posthogApiKey) {
  const posthogConfig = {
    api_host: posthogApiHost,
    ui_host: 'https://eu.posthog.com',
    person_profiles: "always",
    enable_heatmaps: true,
    persistence: hasAnalyticsConsent ? "localStorage" : "memory",
    autocapture: false,
  };

  if (!hasAnalyticsConsent && visitorHash) {
    posthogConfig.bootstrap = { distinctID: visitorHash };
  }

  posthog.init(posthogApiKey, posthogConfig);

  const autocaptureSeed = posthog.get_distinct_id() || visitorHash;
  const autocaptureEnabledForVisitor =
    bucketSeed(autocaptureSeed) < posthogAutocaptureSampleRate;

  posthog.set_config({
    autocapture: autocaptureEnabledForVisitor
      ? {
          element_attribute_ignorelist: ["data-phx-id"],
        }
      : false,
  });
}
window.posthog = posthog;

import { plugin } from "@appsignal/plugin-window-events";

import Appsignal from "@appsignal/javascript";
const appsignal = new Appsignal({
  revision: appRevision,
  key: askey,
  ignoreErrors: [
    /"timeout":\s*true/, // Ignores errors where the message includes {"timeout": true}
    /unable to push hook event\. LiveView not connected/, // Ignores errors about LiveView connectivity
    /"error":\s*"noconnection"/, // Ignores errors with "error": "noconnection"
    /.*failed because the user didn't interact with the document first.*/, // can't autoplay
    /.*request was interrupted because video-only background media was paused to save power.*/, // can't autoplay
    /.*ResizeObserver loop completed with undelivered notifications.*/, // not sure, search suggests maybe lastpass extension or something?
    /.*request was interrupted by a new load request.*/, //video (?)
    /.*request was interrupted by a call to pause.*/, //video (?)
    /.*The operation was aborted.*/,
    /.*The fetching process for the media resource was aborted by the user agent at the user's request*/,
    /.*The play method is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.*/,
    /.*The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.*/,
    /.*An internal error was encountered in the Indexed Database server*/,
    /.*SyntaxError: Can't create duplicate variable: 'MinHeightToDisplayTitle'*/, //?
    /.*Can't create duplicate variable: 'MinHeightToDisplayTitle''*/, //?
    /.*The fetching process for the media resource was aborted by the user agent at the user's request.*/,
    /.*play\(\) can only be initiated by a user gesture.*/,
    /.*The play\(\) request was interrupted because the media was removed from the document.*/,
    /.*isConnected*/, //seems to be coming from liveview js, not our code?
    /.*timeout*/, //seems to be coming from liveview js, not our code?
  ],
});

appsignal.use(plugin({}));

import "@github/markdown-toolbar-element";
import topbar from "topbar";

let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");

let currentScrollY;
let searchRoute;

let liveSocket = new LiveSocket(`${appBasePath}/live`, Socket, {
  hooks: {
    Transcript,
    ScrollIntoViewOnFocus,
    AutoWidthInput,
    Banner,
    Video,
    ThumbnailPreviewVideo,
    Suggestion,
    SearchSuggestions,
    CopyText,
    DateRangePreview,
    Flash,
    FocusElement,
    Diff,
    SyncCursorVideo,
    Markdown,
    ManageVideoPlayer,
    LoadMoreOnScroll,
    Bumper,
    OrderPlaylist,
    TimeInput,
    ResponsiveDropdown,
    MarkdownComplete,
    BodyScroll,
    AlertBox,
    WebShare,
    AudioWave,
    TallyEmbed,
    ThemeToggle,
    Tooltip,
    AnimateFavorites,
    PwywSlider,
    DonutChart,
    DonationAmount,
    DragScroll,
    IframeResize,
    KeyboardAvoidingPopover,
    StickyIfFits,
    AutocompleteInput,
    WhatAreOthersWatchingUpdates,
    CalculateTranscriptTop: {
      mounted() {
        // Listen for LiveView event
        this.calculateTranscriptTop();

        // Listen for custom event
        window.addEventListener(
          "js:calculate-transcript-top",
          this.calculateTranscriptTop(),
        );
      },
      updated() {
        this.calculateTranscriptTop();
      },
      calculateTranscriptTop() {
        const transcript = document.getElementById("transcript-content")
          ? document.getElementById("transcript-content")
          : document.getElementById("transcript");
        if (transcript) {
          document.documentElement.style.setProperty(
            "--transcript-top",
            transcript.getBoundingClientRect().top + "px",
          );
        }
      },
      destroyed() {
        window.removeEventListener(
          "js:calculate-transcript-top",
          this.calculateTranscriptTop(),
        );
      },
    },
    RestoreScroll: {
      mounted() {
        this.handleEvent("restore_scroll", () => {
          if (currentScrollY) {
            window.scrollTo(0, currentScrollY);
            currentScrollY = null;
          }
        });
      },
    },
    SaveSearchSession: {
      mounted() {
        this.saveRoute();
      },
      updated() {
        this.saveRoute();
      },
      saveRoute() {
        const route = `${window.location.pathname}${window.location.search}`;
        if (window.location.pathname === `${appBasePath}/search`) {
          sessionStorage.setItem("last_search_route", route);
        }
      },
    },
    StoreScroll: {
      mounted() {
        this.el.addEventListener("click", (e) => {
          currentScrollY = window.scrollY;
          searchRoute = `${window.location.pathname}${window.location.search}`;
        });
      },
    },
    GoBackToSearch: {
      mounted() {
        this.el.addEventListener("click", (e) => {
          e.preventDefault();
          const route =
            sessionStorage.getItem("last_search_route") || searchRoute;
          if (route) {
            this.liveSocket.js().navigate(route);
          } else {
            this.liveSocket.js().navigate(`${appBasePath}/search`);
          }
        });
      },
      destroyed() {
        searchRoute = null;
      },
    },
    LanguagePicker: {
      mounted() {
        this.messageHandler = (e) => {
          if (e.data.type === "openLanguageModal") {
            this.pushEventTo("#language-picker", "open_language_picker", {});
          }
        };
        window.addEventListener("message", this.messageHandler);
      },
      destroyed() {
        if (this.messageHandler) {
          window.removeEventListener("message", this.messageHandler);
          this.messageHandler = null;
        }
      },
    },
    DelayedEnabled: {
      mounted() {
        setTimeout(() => {
          this.enable();
        }, 1000);
      },
      updated() {
        this.enable();
      },
      enable() {
        if (this.el.dataset.review != "true") {
          this.el.classList.remove("pointer-events-none", "opacity-50");
        }
      },
    },
  },
  dom: {
    onBeforeElUpdated(from, _to) {
      preserveFocusedTranscriptTextAreaScroll(from);
    },
  },
  params: {
    _csrf_token: csrfToken,
    posthog_distinct_id: posthogApiKey ? posthog.get_distinct_id() : null,
    posthog_session_id:
      posthogApiKey && typeof posthog.get_session_id === "function"
        ? posthog.get_session_id()
        : null,
    fly_prefer_instance_id: flyInstanceId,
  },
});

// connect if there are any LiveViews on the page
liveSocket.connect();

// Live reload: stream server logs to web console and HEEx jump-to-definition (c/d + click)
window.addEventListener("phx:live_reload:attached", ({ detail: reloader }) => {
  console.log("phx:live_reload:attached", reloader);
  reloader.enableServerLogs();

  const copyFileLine = (fileLineApp) => {
    if (!fileLineApp) return;
    const [file, line] = fileLineApp;
    const text = `${file}:${line}`;
    navigator.clipboard.writeText(text).then(
      () => console.log(`Copied to clipboard: ${text}`),
      () => console.error("Failed to copy to clipboard", text),
    );
  };

  let keyDown;
  window.addEventListener("keydown", (e) => (keyDown = e.key));
  window.addEventListener("keyup", () => (keyDown = null));
  window.addEventListener(
    "click",
    (e) => {
      if (keyDown === "c") {
        e.preventDefault();
        e.stopImmediatePropagation();
        copyFileLine(reloader.closestCallerFileLine(e.target));
      } else if (keyDown === "d") {
        e.preventDefault();
        e.stopImmediatePropagation();
        copyFileLine(reloader.closestDefFileLine(e.target));
      }
    },
    true,
  );
  window.liveReloader = reloader;
});

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
// window.liveSocket = liveSocket;

window.setCanAutoplay = setCanAutoplay;

window.addEventListener("js:enable-autoplay", () => {
  setCanAutoplay(true);
});

window.addEventListener("gems:blur", (event) => {
  const { target } = event;
  target.blur();
});

window.addEventListener("phx:store-session", async (event) => {
  const token = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute("content");

  const url = `${appBasePath}/store-session`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": token,
    },
    body: JSON.stringify(event.detail.message),
  });

  window.location.href = event.detail.current_path;
});

const target = "*"; // Potentially switch * to a specific origin
let lastPath = null;

const getPathname = (href) => {
  if (href.startsWith("/")) return href.split("?")[0].split("#")[0];
  try {
    return new URL(href).pathname;
  } catch {
    return `/${href}`;
  }
};

const triggerPathChange = (href, patch) => {
  // Extract path for tracking
  let path = href.startsWith("/")
    ? href
    : new URL(href).href.replace(`${new URL(href).origin}/`, "");

  if (lastPath === path) return;

  setCanAutoplay(!!lastPath);
  lastPath = path;

  if (posthogApiKey) posthog.capture("$pageview");
};

window.addEventListener(
  "phx:navigate",
  ({ detail: { href, patch } } = params) => {
    triggerPathChange(href, patch);
  },
);

topbar.config({
  barColors: { 0: "#f7cc4c " },
  shadowColor: "rgba(0, 0, 0, .5)",
  shadowBlur: 5,
  barThickness: 3,
});

let topBarScheduled = undefined;
window.addEventListener("phx:page-loading-start", (info) => {
  if (!topBarScheduled) {
    topBarScheduled = setTimeout(() => topbar.show(), 250);
  }
});
window.addEventListener("phx:page-loading-stop", (info) => {
  if (info.detail.kind == "initial") triggerPathChange(info.detail.to, false);
  clearTimeout(topBarScheduled);
  topBarScheduled = undefined;
  topbar.hide();
  const initialBar = document.getElementById("initial-html-top-bar");
  if (initialBar) {
    initialBar.classList.add("fade-out");
    initialBar.addEventListener("transitionend", () => initialBar.remove());
  }
});

const timeouts = {};
window.addEventListener("delayed-exec", (e) => {
  clearTimeout(timeouts[e.detail.id]);
  timeouts[e.detail.id] = setTimeout(() => {
    liveSocket.execJS(e.target, e.target.getAttribute(e.detail.target));
  }, e.detail.timeout);
});

window.addEventListener("clear-delayed-exec", (e) => {
  clearTimeout(timeouts[e.detail.id]);
});

// Handle page reload after terms acceptance
window.addEventListener("phx:reload_page", () => {
  window.location.reload();
});

// Scroll an element into view
window.addEventListener("phx:scroll-to", (event) => {
  const el = event.target;
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
});

// Scroll to an element by ID (used via push_event)
window.addEventListener("phx:scroll_to", (event) => {
  const id = event.detail && event.detail.id;
  if (id) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
});

// Scroll to an element by ID and center it when possible (used via push_event)
window.addEventListener("phx:scroll_to_and_center", (event) => {
  const id = event.detail && event.detail.id;
  if (!id) return;

  const el = document.getElementById(id);
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const maxScrollY =
    document.documentElement.scrollHeight -
    document.documentElement.clientHeight;

  const canFitInViewport = rect.height <= viewportHeight;
  const idealScrollY =
    window.scrollY + rect.top - (viewportHeight / 2 - rect.height / 2);
  const clampedScrollY = Math.min(Math.max(idealScrollY, 0), maxScrollY);
  const canCenter =
    canFitInViewport && Math.abs(clampedScrollY - idealScrollY) < 1;

  if (canCenter) {
    setTimeout(() => {
      window.scrollTo({ top: clampedScrollY, behavior: "smooth" });
    }, 250); //doesn't work on live navigation without this
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
});
